import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridToolbarButton, GridOptions } from '@app/core/components/grid/grid.types';
import { Item } from '@app/core/components/listbox/listbox.component';
import { DataTaskService } from '@app/core/services/data-task.service';
import { ModalService } from '@app/core/services/modal.service';
import { EconomyService, PriceListItem } from '@app/services/economy.service';
import { LogisticService, StockplaceItem } from '@app/services/logistic.service';
import { DoDataImpImportDialogComponent } from './do-data-imp-import-dialog/do-data-imp-import-dialog.component';
import { firstValueFrom } from 'rxjs';
import { DoDataImpGet, DoDataImpService, GetDoDataImpParams } from '@app/services/do-data-imp.service';
import { ProductService, ProductTypeItem } from '@app/services/product.service';

@Component({
    selector: 'tt-do-data-imp',
    templateUrl: './do-data-imp.component.html',
    styleUrls: ['./do-data-imp.component.css'],
})
export class DoDataImpComponent implements OnChanges, OnInit {
    @Input()
    public params?: GetDoDataImpParams;

    public get?: DoDataImpGet;

    public pricelists: PriceListItem[] = [];

    public languages: Item[] = [];

    public productGroups: Item[] = [];

    public productTypes: ProductTypeItem[] = [];

    public stockplaces: StockplaceItem[] = [];

    public grid?: GridOptions;

    constructor(
        private datatask: DataTaskService,
        private doDataImp: DoDataImpService,
        private economy: EconomyService,
        private logistic: LogisticService,
        private product: ProductService,
        private modal: ModalService,
        private dialog: MatDialog,
    ) {}

    public openUrl() {
        if (!this.get?.url_excel_template) return;

        window.open(this.get.url_excel_template);
    }

    private async loadPricelists() {
        this.pricelists = await this.economy.getPriceLists();
    }

    private async loadLanguages() {
        this.languages = await this.getLanguages();
    }

    private async loadProductGroups() {
        this.productGroups = await this.product.getProductGroups({ add_all: '1' });
    }

    private async loadProductTypes() {
        this.productTypes = await this.product.getProductTypes({ add_all: '1' });
    }

    private async loadStockplaces() {
        this.stockplaces = await this.logistic.getStockplaces({ add_all: '1' });
    }

    private async loadGet() {
        if (!!this.params) {
            this.get = await this.doDataImp.getDoDataImp(this.params);
        }
    }

    private getLanguages() {
        return this.datatask.Post(57);
    }

    private createGrid() {
        if (!!this.get) {
            this.grid = new Grid()
                .setRememberId(this.get.view_id_grid)
                .setLoadDataFunction({ method: +this.get.p2_datatask_keyno_grid, parameters: () => this.get })
                .setCustomToolbarButtons([this.buildImportFromExcelGridToolbarButton(), this.buildRetrieveFromDbGridToolbarButton()])
                .setExcelExportConfig({ useColnameForHeaders: true, excludedColumns: ['is_selected', 'errorcode', 'errormessage'] })
                .setProgressModalOptions({ showCancelButton: true });
        }
    }

    public readGrid() {
        this.grid?.gridfunc?.read();
    }

    private async init() {
        await this.loadGet();
        this.createGrid();
    }

    ngOnInit(): void {
        this.loadPricelists();
        this.loadLanguages();
        this.loadProductGroups();
        this.loadProductTypes();
        this.loadStockplaces();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            delete this.grid;
            this.init();
        }
    }

    private buildImportFromExcelGridToolbarButton(): GridToolbarButton {
        return {
            name: 'dodataimp_from_excel',
            text: 'dodataimp_from_excel',
            translate: true,
            func: () => this.openImportModal(),
            icon: 'far fa-file-import',
            cssClass: 'tt-button tt-button--primary im-grid-btn-xs-r',
        };
    }

    private buildRetrieveFromDbGridToolbarButton(): GridToolbarButton {
        return {
            name: 'dodataimp_retrieve_from_db',
            text: 'dodataimp_retrieve_from_db',
            translate: true,
            func: () => this.fillFromDb(),
            icon: 'fa-sync-alt',
            cssClass: 'tt-button tt-button--primary im-grid-btn-xs-r',
            spinOnAction: true,
            disabled: () => {
                if (!!this.get && this.get.proc_name_fill > '') {
                    return false;
                } else {
                    return 'hidden';
                }
            },
        };
    }

    private async openImportModal() {
        const dialogRef = this.dialog.open(DoDataImpImportDialogComponent, { data: this.get, width: '95%', height: '90%' });

        const result = await firstValueFrom(dialogRef.beforeClosed());

        if (result) {
            this.readGrid();
        }
    }

    private async fillFromDb() {
        if (!this.get) return;

        try {
            const { errorcode, errormessage } = await this.doDataImp.fillFromDb(this.get);

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                this.readGrid();
            }
        } catch (error) {
            console.error(error);
        }
    }
}
